import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  BadgeCheck,
  CalendarRange,
  Clock,
  Download,
  Lock,
  PoundSterling,
  Save,
  CheckCircle2,
} from 'lucide-react';
import { buildBootcampUrl } from '../utils/leadCapture';
import { getResourcesForItem, getResourceTypeColor, getResourceTypeIcon } from '../data/roadmapResources';
import { saveRoadmapToPortal, isUserAuthenticated } from '../utils/saveRoadmap';
import { checkLearningAccess } from '../utils/learningAccess';
import { generateRoadmapPdf } from '../utils/roadmapPdfGenerator';

function groupByMonth(roadmap, maxMonth) {
  if (!roadmap) return [];
  const total = Number(roadmap.totalMonths) || 0;
  const limit = Math.min(total || 0, Number(maxMonth) || total);
  const items = Array.isArray(roadmap.items) ? roadmap.items : [];

  const months = Array.from({ length: limit }, (_, idx) => idx + 1).map((m) => {
    // Filter items for this month
    const monthItems = items.filter((i) => {
      // Support both old format (month) and new format (startMonth/endMonth)
      if (i?.startMonth !== undefined && i?.endMonth !== undefined) {
        return i.startMonth <= m && i.endMonth >= m;
      }
      // Fallback to old format
      return (i?.month ?? 0) === m;
    });
    
    // Sort by order property to preserve drag-and-drop order
    // Items without order property will be sorted to the end
    const sortedItems = monthItems.sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : Infinity;
      const orderB = b.order !== undefined ? b.order : Infinity;
      return orderA - orderB;
    });
    
    return {
      month: m,
      items: sortedItems,
    };
  });
  return months;
}

function formatTitle(roadmap, title) {
  if (title) return title;
  const total = Number(roadmap?.totalMonths) || 0;
  return total ? `Your ${total}-Month Career Roadmap` : 'Your Career Roadmap';
}

function LockedContinuation({ remainingMonths }) {
  if (!remainingMonths) return null;
  return (
    <div className="relative mt-10">
      <div className="absolute inset-x-0 -top-6 h-16 bg-gradient-to-b from-slate-900/0 to-slate-900 pointer-events-none" />
      <div className="rounded-2xl border border-slate-800 bg-slate-950/30 p-6 overflow-hidden">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <Lock className="h-4 w-4 text-green-300" />
          {remainingMonths} more months locked
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60 blur-[1.5px] select-none">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-slate-800 bg-slate-950/40 p-5"
            >
              <div className="h-4 w-40 rounded bg-slate-800" />
              <div className="mt-3 space-y-2">
                <div className="h-3 w-full rounded bg-slate-800" />
                <div className="h-3 w-5/6 rounded bg-slate-800" />
              </div>
              <div className="mt-4 flex gap-3">
                <div className="h-3 w-20 rounded bg-slate-800" />
                <div className="h-3 w-20 rounded bg-slate-800" />
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/10 to-slate-900/80" />
      </div>
    </div>
  );
}

function ResourcesModal({ selectedItem, onClose }) {
  const itemTitle = selectedItem?.item?.title || selectedItem?.item?.name;
  const resources = selectedItem?.resources || [];

  return (
    <div className="fixed inset-0 z-[110]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 text-white shadow-2xl overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-label={`Resources for ${itemTitle || 'roadmap item'}`}
        >
          <div className="p-5 border-b border-slate-800 bg-slate-950/40">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-green-300">Revision resources</div>
                <div className="mt-1 text-lg font-extrabold text-white truncate">
                  {itemTitle}
                </div>
                {selectedItem?.item?.description && (
                  <div className="mt-1 text-sm text-slate-300">
                    {selectedItem.item.description}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 hover:bg-white/5 text-slate-200"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {resources.length > 0 ? (
              <div className="space-y-3">
                {resources.map((resource, idx) => (
                  <a
                    key={`${resource.url}-${idx}`}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-xl border border-slate-800 bg-slate-950/30 p-4 hover:bg-slate-950/50 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-2xl leading-none">{getResourceTypeIcon(resource.type)}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-bold text-white">{resource.title}</div>
                          <span
                            className={[
                              'text-[11px] px-2 py-0.5 rounded-full border font-bold',
                              getResourceTypeColor(resource.type),
                            ].join(' ')}
                          >
                            {resource.type}
                          </span>
                          <span
                            className={[
                              'text-[11px] px-2 py-0.5 rounded-full border font-bold',
                              resource.free
                                ? 'bg-green-500/10 text-green-200 border-green-500/20'
                                : 'bg-yellow-500/10 text-yellow-200 border-yellow-500/20',
                            ].join(' ')}
                          >
                            {resource.free ? 'Free' : 'Paid'}
                          </span>
                        </div>
                        {resource.description && (
                          <div className="mt-1 text-sm text-slate-300">{resource.description}</div>
                        )}
                        <div className="mt-2 text-xs text-slate-400 truncate">{resource.url}</div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-6 text-center">
                <div className="text-lg font-extrabold text-white">No resources yet</div>
                <div className="mt-1 text-sm text-slate-300">
                  We’ll add resources for this item soon. For now, search the topic name and start with official docs.
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-slate-800 bg-slate-950/30 flex items-center justify-between">
            <div className="text-xs text-slate-400">
              {resources.length} resource{resources.length === 1 ? '' : 's'}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RoadmapTimeline({
  roadmap,
  title,
  subtitle,
  maxMonth,
  isUnlocked = true,
  fileName = 'tekyu-roadmap.pdf',
  showBootcampLinks = true,
  roleKey,
}) {
  const containerRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const bootcampUrl = buildBootcampUrl({
    utmMedium: 'roadmap',
    utmCampaign: 'bootcamp',
  });

  useEffect(() => {
    isUserAuthenticated().then(setIsAuthenticated);
    checkLearningAccess().then(setIsPro);
  }, []);

  const months = useMemo(() => groupByMonth(roadmap, maxMonth), [roadmap, maxMonth]);
  const totalMonths = Number(roadmap?.totalMonths) || 0;
  const shownMonths = months.length;
  const remainingMonths = Math.max(0, totalMonths - shownMonths);

  const openResources = (item) => {
    if (!isUnlocked) return;
    const itemTitle = item?.title || item?.name;
    const resources = getResourcesForItem(itemTitle);
    setSelectedItem({ item, resources });
  };

  const onDownloadPdf = async () => {
    try {
      setIsExporting(true);
      await generateRoadmapPdf({
        roadmap,
        title: formatTitle(roadmap, title),
        subtitle,
        fileName,
      });
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const onSaveToPortal = async () => {
    if (!isAuthenticated) {
      setSaveError('Please sign in to save roadmaps');
      setTimeout(() => setSaveError(null), 5000);
      return;
    }
    if (!isPro) {
      setSaveError('Sign in to your free account to save this roadmap.');
      setTimeout(() => setSaveError(null), 5000);
      return;
    }
    if (!roleKey) {
      setSaveError('Unable to determine roadmap type');
      setTimeout(() => setSaveError(null), 5000);
      return;
    }
    
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveRoadmapToPortal({
        roadmap,
        roleKey,
        title,
        subtitle,
        totalMonths: roadmap?.totalMonths || 6,
      });
      if (result.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setSaveError(result.error || 'Failed to save roadmap. Please try again.');
        setTimeout(() => setSaveError(null), 5000);
      }
    } catch (error) {
      console.error('Error saving roadmap:', error);
      setSaveError(error.message || 'Failed to save roadmap. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (!roadmap) return null;

  return (
    <section className="mx-auto max-w-6xl">
      <div
        ref={containerRef}
        className="rounded-3xl border border-slate-800 bg-gradient-to-b from-slate-950/40 to-slate-900/20 shadow-2xl overflow-hidden"
      >
        <div className="relative px-6 sm:px-8 py-8 border-b border-slate-800">
          <div className="absolute inset-0 opacity-60 pointer-events-none">
            <div className="absolute -top-40 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-green-400/10 blur-3xl" />
            <div className="absolute -bottom-48 right-0 h-96 w-96 rounded-full bg-emerald-400/5 blur-3xl" />
          </div>

          <div className="relative flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                {formatTitle(roadmap, title)}
              </h2>
              {subtitle && <div className="mt-2 text-sm text-slate-300">{subtitle}</div>}
              {!isUnlocked && (
                <div className="mt-3 text-sm text-slate-300">
                  Previewing the first <span className="font-semibold text-green-300">{shownMonths}</span> months.
                  Unlock to reveal the full roadmap.
                </div>
              )}
            </div>

            {isUnlocked && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  {isAuthenticated && roleKey && (
                    <button
                      type="button"
                      onClick={onSaveToPortal}
                      disabled={saving || saved}
                      className="inline-flex items-center justify-center rounded-xl border border-green-400/30 bg-green-400/10 px-4 py-2.5 text-sm font-bold text-green-300 hover:bg-green-400/20 transition-colors disabled:opacity-60"
                    >
                      {saved ? (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Saved
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {saving ? 'Saving…' : isPro ? 'Save to Portal' : 'Sign in to save'}
                        </>
                      )}
                    </button>
                  )}
                  {!isPro && isAuthenticated && (
                    <a
                      href="/portal"
                      className="inline-flex items-center justify-center rounded-xl border border-yellow-400/30 bg-yellow-400/10 px-4 py-2.5 text-sm font-bold text-yellow-300 hover:bg-yellow-400/20 transition-colors"
                    >
                      Open your free account
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={onDownloadPdf}
                    disabled={isExporting}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-950/40 px-4 py-2.5 text-sm font-bold text-slate-100 hover:bg-slate-950/70 transition-colors disabled:opacity-60"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {isExporting ? 'Preparing PDF…' : 'Download PDF'}
                  </button>
                </div>
                {saveError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                    {saveError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="relative px-6 sm:px-8 py-8 bg-slate-900/10">
          <div className="absolute inset-0 pointer-events-none opacity-[0.08] [background-image:radial-gradient(#34d399_1px,transparent_1px)] [background-size:18px_18px]" />

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-green-300/60 via-green-400/40 to-green-300/20" />

            <div className="space-y-12">
              {months.map(({ month, items }) => (
                <div key={month} className="relative">
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1">
                    <div className="h-3 w-3 rounded-full bg-green-300 shadow-[0_0_0_6px_rgba(34,197,94,0.12)]" />
                  </div>

                  <div className="flex justify-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-800 bg-slate-950/60 px-4 py-2 text-sm font-extrabold text-green-300 shadow-lg">
                      <span>Month {month}</span>
                      <span className="text-[11px] font-bold text-slate-300">
                        {items.length} {items.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {items.map((item, idx) => {
                      const isCert = item?.type === 'certification';
                      const isLeft = idx % 2 === 0;
                      return (
                        <div
                          key={`${month}-${item?.title || item?.name || idx}`}
                          className={isLeft ? 'md:pr-6' : 'md:pl-6'}
                        >
                          <div
                            className={[
                              'rounded-2xl border bg-slate-950/35 p-5 shadow-xl',
                              'border-slate-800 hover:border-slate-700 transition-colors',
                              isCert ? 'ring-1 ring-green-400/30' : '',
                            ].join(' ')}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-lg font-extrabold text-white">
                                  {item?.title || item?.name}
                                </div>
                                {item?.description && (
                                  <div className="mt-1 text-sm text-slate-300">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                              {isCert && (
                                <div className="inline-flex items-center gap-1 rounded-full bg-green-400/10 border border-green-400/20 px-2.5 py-1 text-xs font-bold text-green-300">
                                  <BadgeCheck className="h-4 w-4" />
                                  Certification
                                </div>
                              )}
                            </div>

                            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-300">
                              <div className="inline-flex items-center gap-2">
                                <Clock className="h-4 w-4 text-green-300" />
                                {item?.duration ? `${item.duration} week${Number(item.duration) === 1 ? '' : 's'}` : '—'}
                              </div>
                              <div className="inline-flex items-center gap-2">
                                <CalendarRange className="h-4 w-4 text-green-300" />
                                Months {item?.startMonth ?? '—'}–{item?.endMonth ?? '—'}
                              </div>
                              {item?.cost && (
                                <div className="inline-flex items-center gap-2">
                                  <PoundSterling className="h-4 w-4 text-green-300" />
                                  £{item.cost}
                                </div>
                              )}
                            </div>

                            <div className="mt-4 pt-4 border-t border-slate-800/60 flex items-center justify-between gap-3">
                              <button
                                type="button"
                                onClick={() => openResources(item)}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/30 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-slate-950/60 hover:border-slate-700 transition-colors"
                              >
                                <span aria-hidden="true">📚</span>
                                Resources
                                <span className="text-slate-400 font-semibold">
                                  ({getResourcesForItem(item?.title || item?.name).length})
                                </span>
                              </button>
                              <div className="text-xs text-slate-400">
                                Click to open revision resources
                              </div>
                            </div>

                            {showBootcampLinks && item?.coveredInBootcamp && (
                              <div className="mt-4 text-xs text-yellow-300">
                                <a
                                  href={bootcampUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline"
                                >
                                  Covered on Cyber Agoge Bootcamp
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {!isUnlocked && <LockedContinuation remainingMonths={remainingMonths} />}
          </div>
        </div>
      </div>

      {selectedItem && (
        <ResourcesModal selectedItem={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </section>
  );
}

